// ============================================================================
// Users & Roles — BFF client (Phase 1: read paths)
// ----------------------------------------------------------------------------
// Thin wrappers over the authenticated fetch helpers in src/utils/api.js. Every
// call targets the Flask BFF (/api/users*, /api/roles*, /api/events*) — the
// browser never talks to Keycloak directly (see docs/users-roles-api.md §0).
// Endpoints map 1:1 to dtflwr/userconfigs/admin_users_api.py.
// ============================================================================
import { apiGet, apiPut, apiPost, apiDelete } from "../../utils/api";
import { ROLE_META } from "./data/mockUsersRoles";

// Roles we don't surface as "the" role of a pending invite. `member` is a real,
// user-facing built-in so it stays visible (see audit M2); only Keycloak's
// internal technical roles are hidden.
const TECHNICAL = new Set(["user", "offline_access", "uma_authorization"]);

// Superadmin org switcher: when set, scope org-aware reads to this org via ?org=.
let activeOrg = null;
export const setActiveOrg = (org) => { activeOrg = org || null; };
const withOrg = (path) => {
  if (!activeOrg) return path;
  return path + (path.includes("?") ? "&" : "?") + `org=${encodeURIComponent(activeOrg)}`;
};

// Pick the most meaningful role name for a pending-invite row.
const primaryRole = (roles = []) =>
  roles.find((r) => ROLE_META[r]) || roles.find((r) => !TECHNICAL.has(r)) || roles[0] || "";

// --- Members -----------------------------------------------------------------
// BFF call: GET /api/users  (§2.1) — member grid (roles embedded server-side)
export const listMembers = (search) =>
  apiGet(withOrg(`/users${search ? `?search=${encodeURIComponent(search)}` : ""}`));

// BFF call: GET /api/users/{id}/roles  (§2.2)
export const getUserRoles = (id) => apiGet(`/users/${encodeURIComponent(id)}/roles`);

// --- Pending invitations -----------------------------------------------------
// BFF call: GET /api/users?enabled=false  (§3.4). Mapped to the table's shape.
export const listPending = async () => {
  const users = await apiGet(withOrg(`/users?enabled=false`));
  return (users || []).map((u) => ({
    id: u.id,
    email: u.email,
    role: primaryRole(u.roles),
    projects: u.projects || [],
    sent: u.since || "",
    // Real expiry lands with the invite-email flow (Phase 3); placeholder for now.
    expiresInH: 24,
  }));
};

// --- Roles -------------------------------------------------------------------
// BFF call: GET /api/roles  (§4.1) — live Keycloak roles joined with the org's
// DB catalog (permissions, displayName, syncStatus, isSystem).
export const listRoles = () => apiGet(withOrg(`/roles`));

// Create a role in the DB catalog AND push it to the tenant's Keycloak.
// BFF: POST /api/roles  → { name, displayName, description, permissions }
export const createRole = (payload) => apiPost(withOrg(`/roles`), payload, "POST");

// Edit display/description/permissions. BFF: PUT /api/roles/{name}
export const updateRole = (name, payload) =>
  apiPut(withOrg(`/roles/${encodeURIComponent(name)}`), payload);

// Delete a non-system role (catalog + Keycloak). BFF: DELETE /api/roles/{name}
// Server moves any member left roleless to the org's default role and returns
// { reassigned, keptOther, defaultRole }.
export const deleteRole = (name) =>
  apiDelete(withOrg(`/roles/${encodeURIComponent(name)}`));

// Preview the blast radius of deleting a role — which members get demoted to
// the default role vs. keep another role. BFF: GET /api/roles/{name}/deletion-impact
export const getRoleDeletionImpact = (name) =>
  apiGet(withOrg(`/roles/${encodeURIComponent(name)}/deletion-impact`));

// Designate a role as the org's default fallback (also wired into Keycloak's
// realm default-roles). BFF: POST /api/roles/{name}/default
export const setDefaultRole = (name) =>
  apiPost(withOrg(`/roles/${encodeURIComponent(name)}/default`), {}, "POST");

// Retry pushing a catalog role into the tenant Keycloak. BFF: POST /api/roles/{name}/sync
export const syncRole = (name) =>
  apiPost(withOrg(`/roles/${encodeURIComponent(name)}/sync`), {}, "POST");

// Compute the current user's effective permission map from the role catalog.
// Used for defense-in-depth button gating (the BFF re-checks every action).
export const getMyPermissions = async (myRoles = []) => {
  const roles = await listRoles().catch(() => []);
  const merged = {};
  (roles || []).forEach((r) => {
    if (!myRoles.includes(r.name)) return;
    Object.entries(r.permissions || {}).forEach(([k, v]) => {
      merged[k] = (typeof v === "boolean") ? (merged[k] || v) : v;
    });
  });
  return merged;
};

// --- Activity ----------------------------------------------------------------
// BFF call: GET /api/events + GET /api/admin-events  (§5), merged + sorted desc.
export const getActivity = async () => {
  const [logins, admin, audit] = await Promise.all([
    apiGet(withOrg(`/events?max=100`)).catch(() => []),
    apiGet(withOrg(`/admin-events?max=100`)).catch(() => []),
    apiGet(withOrg(`/audit?max=100`)).catch(() => []),
  ]);
  return [...(logins || []), ...(admin || []), ...(audit || [])]
    .filter((e) => e && e.ts)
    .sort((a, b) => String(b.ts).localeCompare(String(a.ts)));
};

// BFF call: GET /api/sessions  (§5) — aggregated active sessions (org-scoped)
export const getSessions = () => apiGet(withOrg(`/sessions`));

// --- Mutations (Phase 2) -----------------------------------------------------
const enc = encodeURIComponent;

// BFF call: PUT /api/users/{id}  (§2.4) — name / enabled / site scope
export const updateMember = (id, body) => apiPut(`/users/${enc(id)}`, body);

// BFF call: DELETE /api/users/{id}  (§2.5)
export const removeMember = (id) => apiDelete(`/users/${enc(id)}`);

// BFF call: DELETE /api/users/{id}/sessions  (§2.6) — force re-login
export const killSessions = (id) => apiDelete(`/users/${enc(id)}/sessions`);

// BFF call: POST /api/users/{id}/roles  (§4.4) — grant (server also kills sessions)
export const grantRoles = (id, roles) =>
  apiPost(`/users/${enc(id)}/roles`, { roles }, "POST");

// BFF call: DELETE /api/users/{id}/roles  (§4.5) — revoke (server also kills sessions)
export const revokeRoles = (id, roles) =>
  apiPost(`/users/${enc(id)}/roles`, { roles }, "DELETE");

// --- Invite (Phase 3) --------------------------------------------------------
// Atomic invite: create user → assign role → send setup email (server rolls
// back on failure). BFF: POST /api/invitations (wraps §3.1–3.3).
export const createInvitation = (payload) => apiPost(`/invitations`, payload, "POST");

// BFF call: PUT /api/users/{id}/invite-email  (§3.5) — resend setup email
export const resendInvite = (id) => apiPut(`/users/${enc(id)}/invite-email`, {});

// Public: the invited user sets their password via the emailed link.
// BFF: POST /api/invitations/accept  (no auth — the one-time token authorizes)
export const acceptInvitation = (token, password) =>
  apiPost(`/invitations/accept`, { token, password }, "POST");

// Caller's org + the address invites are sent from (per-org sender indicator).
// BFF: GET /api/my-org
export const getMyOrg = () => apiGet(withOrg(`/my-org`));

// --- Organizations (Phase 5) -------------------------------------------------
// BFF: GET /api/organizations (superadmin) — list with sender/domains/members
export const getOrganizations = () => apiGet(`/organizations`);
export const createOrganization = (name, domains = []) =>
  apiPost(`/organizations`, { name, domains }, "POST");

// Per-org email sender config (org_admin own; superadmin via ?org=)
export const getOrgEmailConfig = (org) =>
  apiGet(`/my-org/email-config${org ? `?org=${encodeURIComponent(org)}` : ""}`);
export const setOrgEmailConfig = (config, org) =>
  apiPut(`/my-org/email-config${org ? `?org=${encodeURIComponent(org)}` : ""}`, config);

// Domain verification
export const addOrgDomain = (domain, org) =>
  apiPost(`/my-org/domains${org ? `?org=${encodeURIComponent(org)}` : ""}`, { domain }, "POST");
export const verifyOrgDomain = (domain, org, manual = false) =>
  apiPost(`/my-org/domains/verify${org ? `?org=${encodeURIComponent(org)}` : ""}`, { domain, manual }, "POST");

// --- Per-tenant Keycloak (true federation) ----------------------------------
// PUBLIC: which Keycloak this tenant logs in against (subdomain → org).
// BFF: GET /api/tenant-config — used by the login bootstrap.
export const getTenantConfig = (host) =>
  apiGet(`/tenant-config${host ? `?host=${encodeURIComponent(host)}` : ""}`);

// Superadmin: register/list a business's own Keycloak connection.
// BFF: GET/POST /api/kc-connections (secret server-side only; preflighted).
export const listKcConnections = () => apiGet(`/kc-connections`);
export const registerKcConnection = (payload) =>
  apiPost(`/kc-connections`, payload, "POST");

const initialsOf = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const sameSet = (a = [], b = []) =>
  JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());

// Orchestrates an Edit-User save: PUT profile/scope, grant/revoke role diff,
// and force re-login when access changed (grant/revoke already kill sessions
// server-side; a scope-only change kills them here). Returns the updated row.
export const saveMemberEdits = async (original, { name, roles, projects }) => {
  const [firstName, ...rest] = (name || "").trim().split(/\s+/);
  const lastName = rest.join(" ");

  await updateMember(original.id, {
    firstName: firstName || "",
    lastName,
    site_ids: projects || [],
  });

  const before = original.roles || [];
  const added = (roles || []).filter((r) => !before.includes(r));
  const removed = before.filter((r) => !(roles || []).includes(r));
  if (added.length) await grantRoles(original.id, added);
  if (removed.length) await revokeRoles(original.id, removed);

  const scopeChanged = !sameSet(original.projects, projects);
  const rolesChanged = added.length > 0 || removed.length > 0;
  if (scopeChanged && !rolesChanged) await killSessions(original.id);

  return {
    ...original,
    name,
    initials: initialsOf(name),
    roles: roles || [],
    projects: projects || [],
    accessChanged: scopeChanged || rolesChanged,
  };
};
